import { FileMetadata } from "@domain/schemas/BI";
import type { IAuthenticatedRequestHandlerService } from "@application/contracts/services";
import { API_ENDPOINTS } from "@infrastructure/InfrastructureConstants";
import { runtimeConfig } from "@utils/runtimeConfig/runtimeConfig";
import { TYPES } from "@utils/inversifyJS/types";
import axios, { CancelToken } from "axios";
import { inject, injectable } from "inversify";

@injectable()
export class LibraryRepository {
  private static readonly CHUNK_SIZE_BYTES = 8 * 1024 * 1024;

  private readonly baseUrl = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.libraries}`;
  private readonly uploadInitUrl = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.librariesUploadInit}`;
  private readonly uploadChunkUrl = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.librariesUploadChunk}`;
  private readonly uploadCompleteUrl = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.librariesUploadComplete}`;

  @inject(TYPES.IAuthenticatedRequestHandlerService)
  private _requestHandlerService!: IAuthenticatedRequestHandlerService;

  public async getLibraries(): Promise<FileMetadata[]> {
    return this._requestHandlerService.handleRequestAsync<FileMetadata[]>(() => axios.get<FileMetadata[]>(this.baseUrl));
  }

  public async uploadLibrary(file: File, folder: string, onProgress?: (progress: number) => void, cancelToken?: CancelToken): Promise<void> {
    const totalChunks = Math.max(1, Math.ceil(file.size / LibraryRepository.CHUNK_SIZE_BYTES));

    const uploadId = await this._requestHandlerService.handleRequestAsync<string>(() =>
      axios.post<string>(
        this.uploadInitUrl,
        {
          fileName: file.name,
          folder,
          fileLength: file.size,
        },
        {
          timeout: 24 * 60 * 60 * 1000,
          cancelToken,
        },
      ),
    );

    const partTokens: string[] = [];
    let completedBytes = 0;

    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      const start = chunkIndex * LibraryRepository.CHUNK_SIZE_BYTES;
      const end = Math.min(start + LibraryRepository.CHUNK_SIZE_BYTES, file.size);
      const chunk = file.slice(start, end);
      const partNumber = chunkIndex + 1;

      const partToken = await this._requestHandlerService.handleRequestAsync<string>(() =>
        axios.post<string>(this.uploadChunkUrl, chunk, {
          params: {
            uploadId,
            partNumber,
            fileName: file.name,
            folder,
          },
          headers: {
            "Content-Type": "application/octet-stream",
          },
          timeout: 24 * 60 * 60 * 1000,
          cancelToken,
          onUploadProgress: (progressEvent) => {
            if (!onProgress) {
              return;
            }

            const loaded = Math.min(completedBytes + (progressEvent.loaded ?? 0), file.size);
            const percentCompleted = Math.min(99, Math.round((loaded * 100) / (file.size || 1)));
            onProgress(percentCompleted);
          },
        }),
      );

      partTokens.push(partToken);
      completedBytes = end;

      if (!onProgress) continue;

      const percentCompleted = chunkIndex === totalChunks - 1 ? 99 : Math.min(99, Math.round((completedBytes * 100) / (file.size || 1)));
      onProgress(percentCompleted);
    }

    await this._requestHandlerService.handleRequestAsync<void>(() =>
      axios.post<void>(
        this.uploadCompleteUrl,
        {
          uploadId,
          partTokens,
          fileName: file.name,
          folder,
          md5Hash: null,
        },
        {
          timeout: 24 * 60 * 60 * 1000,
          cancelToken,
        },
      ),
    );

    if (onProgress) {
      onProgress(100);
    }
  }
}
